import * as React from "react"
import { AlertOctagon, Home, RotateCw } from "lucide-react"

import { Button } from "@/components/ui/button"
import { logger } from "@/lib/logger"
import { t } from "@/lib/i18n"

/**
 * La red bajo la SPA.
 *
 * Sin esto, cualquier excepcion durante el render desmonta el arbol entero y el
 * usuario se queda mirando una pantalla en blanco — sin saber si guardo o no, y
 * sin que nadie se entere nunca. En un producto contable eso no es una molestia:
 * alguien puede creer que aprobo un asiento que nunca se aprobo.
 *
 * Tres decisiones:
 *
 * 1. **Se dice que paso, en el idioma del producto.** Ni "Oops" ni "Something
 *    went wrong": que fallo, que significa para lo que estabas haciendo y como
 *    seguir.
 * 2. **El detalle tecnico existe pero no esta en la cara.** Va detras de un
 *    `<details>`, para poder copiarlo en un reporte.
 * 3. **Se reporta siempre.** Con el `componentStack`, que es lo que dice QUE
 *    componente reviento, y con el `request_id` si el error venia de la API:
 *    ese id es lo que permite encontrar la otra mitad del incidente en el log
 *    del servidor.
 */

interface Props {
  children: React.ReactNode
  /** Etiqueta del ambito que se esta protegiendo. Va al log. */
  ambito?: string
}

interface State {
  error: Error | null
  componentStack: string | null
}

/** El `requestId` sin arrastrar `ApiError` (y con el, Firebase) a esta ruta. */
function requestIdDe(error: unknown): string | null {
  if (error && typeof error === "object" && "requestId" in error) {
    const valor = (error as { requestId?: unknown }).requestId
    if (typeof valor === "string" && valor) return valor
  }
  return null
}

function estadoDe(error: unknown): number | null {
  if (error && typeof error === "object" && "status" in error) {
    const valor = (error as { status?: unknown }).status
    if (typeof valor === "number") return valor
  }
  return null
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, componentStack: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null })
    logger.error(error.message || "error de render no controlado", {
      origen: "ErrorBoundary",
      ambito: this.props.ambito ?? "app",
      tipo: error.name,
      request_id: requestIdDe(error),
      estado: estadoDe(error),
      // El stack del componente dice que reviento; el de JS, donde. Los dos van,
      // el primero en `datos` y el segundo en el campo `stack` del contrato.
      componente: (info.componentStack ?? "").trim().split("\n").slice(0, 6).join(" ").trim(),
      stack: error.stack,
    })
  }

  recargar = () => {
    window.location.reload()
  }

  alInicio = () => {
    // `assign` y no `navigate`: el router puede ser justamente lo que reviento.
    window.location.assign("/")
  }

  render() {
    const { error, componentStack } = this.state
    if (!error) return this.props.children

    const requestId = requestIdDe(error)

    return (
      <div
        role="alert"
        data-testid="error-boundary"
        className="mx-auto flex max-w-lg flex-col px-6 py-20"
      >
        <div className="grid h-9 w-9 place-items-center rounded-full border border-destructive/35 bg-destructive-soft text-destructive">
          <AlertOctagon className="h-4 w-4" />
        </div>

        <h1 className="mt-4 text-lg font-semibold">{t("No se pudo mostrar esta pantalla")}</h1>
        <p className="mt-1.5 max-w-[62ch] text-xs leading-[1.15rem] text-muted-foreground">
          {t("Se interrumpio antes de terminar de dibujarse, asi que lo que ves puede estar incompleto. Nada que hayas confirmado se pierde por esto, pero si estabas guardando algo, no des el cambio por hecho: recarga y verificalo en la lista antes de repetirlo.")}</p>

        {requestId ? (
          <p className="mt-3 text-2xs text-muted-foreground">
            {t("Referencia del incidente:")}{" "}
            <span className="code text-foreground">{requestId}</span>{t(". Citala si lo reportas: con ella se reconstruye que paso en el servidor.")}</p>
        ) : null}

        <div className="mt-5 flex gap-2">
          <Button size="sm" onClick={this.recargar}>
            <RotateCw />
            {t("Recargar la pantalla")}</Button>
          <Button size="sm" variant="outline" onClick={this.alInicio}>
            <Home />
            {t("Volver al inicio")}</Button>
        </div>

        <details className="mt-6 rounded-panel border bg-surface p-3">
          <summary className="cursor-pointer select-none text-2xs text-muted-foreground">
            {t("Detalle tecnico")}</summary>
          <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-2xs leading-[1.05rem] text-muted-foreground">
            {error.name}: {error.message}
            {componentStack ? `\n${componentStack}` : ""}
          </pre>
        </details>
      </div>
    )
  }
}
